import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import PhygitalIcon from "@/components/PhygitalIcon";
import PitchDeckSlides from "@/components/investor/PitchDeckSlides";
import AdvocatePortalPage from "@/pages/AdvocatePortalPage";
import UnifiedTopBar from "@/components/UnifiedTopBar";
import { toast } from "sonner";
import {
  TrendingUp, Bell, BarChart3, Presentation, HeartHandshake,
  RefreshCw, Mail, Calculator, DollarSign, MapPin, Lock,
  ChevronDown, ChevronUp, Filter, Tag, ExternalLink, X, Calendar, Image as ImageIcon,
  Scissors, Sparkles, ChefHat, PartyPopper, Heart, Plane, ShoppingBag, Eye,
  GraduationCap, Leaf, Rocket, Target, TrendingDown, Award, Cannabis
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const INVESTOR_TABS = [
  { id: "updates", label: "Updates", icon: Bell },
  { id: "pitch_deck", label: "Pitch Deck", icon: Presentation },
  { id: "projections", label: "Projections", icon: BarChart3 },
];

const ADVOCATE_TAB = { id: "advocate", label: "Advocate", icon: HeartHandshake };

const FINANCIAL_METRICS = [
  { label: "Founded", value: "Jan 2026", sublabel: "Los Angeles, CA" },
  { label: "Stage", value: "Pre-Seed", sublabel: "Pilot program active" },
  { label: "Categories", value: "11", sublabel: "7 pilot + 4 coming soon" },
  { label: "Target Cities", value: "11", sublabel: "LA, Charlotte, London + 8 more" },
];

// --- Custom Nail Polish Icon to match categoryConfig.js ---
const NailPolishIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M10 2h4v6l1 1v3a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2V9l1-1V2z" />
    <path d="M10 14v6a2 2 0 0 0 2 2v0a2 2 0 0 0 2-2v-6" />
    <line x1="10" y1="5" x2="14" y2="5" />
  </svg>
);

// --- Category color system (synced with categoryConfig.js) ---
const CATEGORY_COLORS = {
  hair:        { iconColor: "#e83e8c", bgColor: "#ffeef4" },
  makeup:      { iconColor: "#9c57f5", bgColor: "#f2e9ff" },
  nails:       { iconColor: "#f15a70", bgColor: "#ffe8ec" },
  lashes:      { iconColor: "#774cda", bgColor: "#ede7f7" },
  events:      { iconColor: "#f9c434", bgColor: "#fff5e6" },
  instructors: { iconColor: "#4a90d9", bgColor: "#e0eeff" },
  culinary:    { iconColor: "#f28c28", bgColor: "#fff3e6" },
  wellness:    { iconColor: "#75d6d6", bgColor: "#e7f5f5" },
  travel:      { iconColor: "#73b7ff", bgColor: "#e7f3ff" },
  dispensary:  { iconColor: "#60d680", bgColor: "#e8fef0" },
  retail:      { iconColor: "#9950fa", bgColor: "#f0e8ff" },
};

// Icons synced with categoryConfig.js presets
const TAG_ICONS = {
  hair: Scissors, makeup: Sparkles, nails: NailPolishIcon, lashes: Eye,
  culinary: ChefHat, events: PartyPopper, wellness: Heart,
  travel: Plane, retail: ShoppingBag, dispensary: Cannabis,
  instructors: Presentation,
  product: Rocket, growth: TrendingUp, milestone: Award, expansion: MapPin,
};

const darkenHex = (hex, factor = 0.35) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`;
};

const getCategoryColor = (tags) => {
  if (!tags || !tags.length) return null;
  for (const tag of tags) {
    if (CATEGORY_COLORS[tag]) return CATEGORY_COLORS[tag];
  }
  return null;
};

// --- Updates Component for Investor Portal ---
const InvestorUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [filterTag, setFilterTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedUpdate, setSelectedUpdate] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [portalRes, feedRes] = await Promise.all([
          fetch(`${API}/api/portal/investor/updates`, { headers: getHeaders() }).catch(() => null),
          fetch(`${API}/api/updates/feed`, { headers: getHeaders() }).catch(() => null),
        ]);
        let all = [];
        if (portalRes?.ok) {
          const data = await portalRes.json();
          all.push(...(data.updates || []));
        }
        if (feedRes?.ok) {
          const feedData = await feedRes.json();
          const feedUpdates = (Array.isArray(feedData) ? feedData : []).map(u => ({
            ...u,
            id: u.update_id || u.id,
            tags: u.target_categories || u.tags || [],
            status: u.is_published !== false ? "published" : "draft",
            image_url: u.image_url || (u.media?.[0]?.url),
            cover_image: u.image_url || u.cover_image || (u.media?.[0]?.url),
          }));
          const existingIds = new Set(all.map(u => u.id || u.update_id));
          all.push(...feedUpdates.filter(u => !existingIds.has(u.id)));
        }
        setUpdates(all);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const allTags = [...new Set(updates.flatMap(u => u.tags || []))];
  const sortedUpdates = [...updates].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const visibleUpdates = filterTag ? sortedUpdates.filter(u => (u.tags || []).includes(filterTag)) : sortedUpdates;

  // Responsive grid: 1 col for 1 item, 2 for 2, max 3 on desktop, max 2 on mobile
  const gridCols = visibleUpdates.length === 1 ? 'grid-cols-1' : visibleUpdates.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-emerald-400" /></div>;

  return (
    <div data-testid="investor-updates-section">
      {/* Filter bar */}
      {allTags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-5 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-emerald-400/60" />
          <button onClick={() => setFilterTag("")}
            className={`text-xs px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${!filterTag ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-500/30' : 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10'}`}
            data-testid="filter-all"
          >All</button>
          {allTags.map(tag => {
            const Icon = TAG_ICONS[tag] || Tag;
            const catColor = CATEGORY_COLORS[tag];
            const isActive = filterTag === tag;
            return (
              <button key={tag} onClick={() => setFilterTag(tag)}
                data-testid={`filter-${tag}`}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 capitalize ${!isActive ? 'bg-white/5 text-white/40 border border-white/10 hover:bg-white/10' : ''}`}
                style={isActive && catColor
                  ? { backgroundColor: catColor.iconColor + '30', color: catColor.iconColor, border: `1px solid ${catColor.iconColor}40` }
                  : isActive ? { backgroundColor: 'rgba(16,185,129,0.3)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.3)' } : {}
                }
              >
                <Icon className="w-3 h-3" />{tag}
              </button>
            );
          })}
        </div>
      )}

      {/* Update cards grid */}
      {visibleUpdates.length > 0 ? (
        <div className={`grid ${gridCols} gap-4`}>
          {visibleUpdates.map((update) => {
            const catColor = getCategoryColor(update.tags);
            const darkBg = catColor ? darkenHex(catColor.iconColor, 0.3) : 'rgb(10, 40, 24)';
            const ctaList = update.cta_buttons || update.buttons || [];
            const singleCta = !ctaList.length && update.cta_url ? [{ label: update.cta_text || "Learn More", url: update.cta_url }] : ctaList;
            return (
              <div
                key={update.update_id}
                className="rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow border"
                style={{ borderColor: catColor ? catColor.iconColor + '25' : 'rgba(255,255,255,0.08)' }}
                data-testid={`investor-update-${update.update_id}`}
                onClick={() => setSelectedUpdate(update)}
              >
                {/* Cover image */}
                {update.cover_image && (
                  <div className="w-full" style={{ aspectRatio: '16/9' }}>
                    <img src={update.cover_image.startsWith('http') ? update.cover_image : `${API}${update.cover_image}`} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {/* Content section with dark category bg */}
                <div className="p-4" style={{ backgroundColor: darkBg }}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="font-semibold text-white text-sm leading-snug">{update.title}</h4>
                    {update.created_at && (
                      <span className="text-[10px] text-white/40 flex-shrink-0 flex items-center gap-0.5 pt-0.5">
                        <Calendar className="w-2.5 h-2.5" />
                        {!update.hide_date && (new Date(update.display_date || update.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }))}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/70 line-clamp-2 leading-relaxed">{update.content}</p>
                  {/* CTA on card */}
                  {singleCta.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {singleCta.map((btn, i) => (
                        <button key={i}
                          className="text-[11px] font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 transition-opacity hover:opacity-90"
                          style={{ backgroundColor: catColor ? catColor.iconColor : '#10b981', color: '#fff' }}
                          onClick={e => { e.stopPropagation(); window.open(btn.url || btn.cta_url, '_blank'); }}
                        >
                          {btn.label || btn.cta_text} <ExternalLink className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="bg-white/[0.03] border-white/[0.06] border-dashed">
          <CardContent className="p-12 text-center">
            <Bell className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-emerald-200/60 font-medium">No updates yet</p>
            <p className="text-white/20 text-sm mt-1">We'll notify you when there's news</p>
          </CardContent>
        </Card>
      )}

      {/* Update Detail Modal -- white bg, black text */}
      <Dialog open={!!selectedUpdate} onOpenChange={() => setSelectedUpdate(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white" style={{ maxHeight: '90vh' }}>
          <DialogTitle className="sr-only">Update Details</DialogTitle>
          {selectedUpdate && (() => {
            const modalCatColor = getCategoryColor(selectedUpdate.tags);
            const modalCtaList = selectedUpdate.cta_buttons || selectedUpdate.buttons || [];
            const modalSingleCta = !modalCtaList.length && selectedUpdate.cta_url ? [{ label: selectedUpdate.cta_text || "Learn More", url: selectedUpdate.cta_url }] : modalCtaList;
            return (
              <div className="overflow-y-auto" style={{ maxHeight: '90vh' }}>
                {selectedUpdate.cover_image && (
                  <div className="w-full" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={selectedUpdate.cover_image.startsWith('http') ? selectedUpdate.cover_image : `${API}${selectedUpdate.cover_image}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-8 space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <h2 className="text-2xl font-bold text-gray-900 leading-tight">{selectedUpdate.title}</h2>
                    {selectedUpdate.created_at && (
                      <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0 pt-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {!selectedUpdate.hide_date && new Date(selectedUpdate.display_date || selectedUpdate.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selectedUpdate.content}
                  </p>

                  {/* Gallery images */}
                  {(selectedUpdate.gallery_images || []).length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {selectedUpdate.gallery_images.map((img, i) => (
                        <div key={i} className="aspect-video rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                          <img src={typeof img === "string" ? img : (img.url || img.image_url)} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* CTA Buttons */}
                  {modalSingleCta.length > 0 && (
                    <div className="flex flex-wrap gap-3 pt-2">
                      {modalSingleCta.map((btn, i) => (
                        <Button
                          key={i}
                          className="text-white"
                          style={{ backgroundColor: modalCatColor ? modalCatColor.iconColor : '#10b981' }}
                          onClick={() => window.open(btn.url || btn.cta_url, '_blank')}
                        >
                          {btn.label || btn.cta_text} <ExternalLink className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Investor Projection Simulator (moved from SupportPortalPage) ---
const InvestorProjectionSimulator = ({ investorPortalId }) => {
  const [rawData, setRawData] = useState(null);
  const [cityTargets, setCityTargets] = useState({});
  const [commissionPct, setCommissionPct] = useState(10);
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({ 1: true, 2: true, 3: true });

  useEffect(() => {
    const load = async () => {
      try {
        let data = null;
        if (investorPortalId) {
          const res = await fetch(`${API}/api/investor/projections/${investorPortalId}`, { headers: getHeaders() });
          if (res.ok) data = await res.json();
        }
        if (!data) {
          const res = await fetch(`${API}/api/admin/financial-projections`, { headers: getHeaders() });
          if (res.ok) data = await res.json();
        }
        if (data) {
          const proj = data.projections;
          if (proj) {
            setRawData(proj);
            const targets = {};
            (proj.cities || []).forEach(c => { targets[c.city_id] = c.total_target || 0; });
            setCityTargets(targets);
          }
          if (data.investor_share?.percentage) setCommissionPct(data.investor_share.percentage);
        }
      } catch (err) { console.error("Failed to load projections:", err); }
      setLoading(false);
    };
    load();
  }, [investorPortalId]);

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-emerald-400" /></div>;
  if (!rawData) return <Card className="bg-white/[0.03] border-white/[0.06] border-dashed"><CardContent className="p-8 text-center"><Calculator className="w-8 h-8 mx-auto mb-2 text-white/10" /><p className="text-sm text-emerald-200/40">No projection data available yet</p></CardContent></Card>;

  const cities = rawData.cities || [];
  const assumptions = rawData.assumptions || {};
  const pricing = rawData.pricing || {};

  const totalMarketSize = cities.reduce((s, c) => s + (c.total_market_size || 0), 0);
  const totalTarget = Object.values(cityTargets).reduce((s, t) => s + (t || 0), 0);
  const payingProviders = totalTarget * (100 - (assumptions.free_percent || 70)) / 100;
  const monthlyPayers = payingProviders * (100 - (assumptions.annual_percent || 30)) / 100;
  const annualPayers = payingProviders * (assumptions.annual_percent || 30) / 100;

  const soloM = monthlyPayers * (assumptions.solo_percent || 20) / 30;
  const teamM = monthlyPayers * (assumptions.team_percent || 8) / 30;
  const luxeM = monthlyPayers * (assumptions.luxe_percent || 2) / 30;
  const soloA = annualPayers * (assumptions.solo_percent || 20) / 30;
  const teamA = annualPayers * (assumptions.team_percent || 8) / 30;
  const luxeA = annualPayers * (assumptions.luxe_percent || 2) / 30;

  const projMRR = soloM * (pricing.solo_monthly || 11) + teamM * (pricing.team_monthly || 44) + luxeM * (pricing.luxe_monthly || 99) + (soloA * (pricing.solo_annual || 110) / 12) + (teamA * (pricing.team_annual || 440) / 12) + (luxeA * (pricing.luxe_annual || 990) / 12);
  const projARR = projMRR * 12;
  const myPayout = projARR * (commissionPct / 100);

  const fmt = (n) => { if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`; if (n >= 1000) return `$${Math.round(n / 1000)}K`; return `$${Math.round(n)}`; };
  const fmtNum = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    if (n >= 1000) return `${Math.round(n / 1000)}K`;
    return `${Math.round(n)}`;
  };

  const phases = [
    { num: 1, label: "Phase 1 (Months 0-6)", color: "bg-emerald-900/40 text-emerald-300 border border-emerald-500/20" },
    { num: 2, label: "Phase 2 (Months 6-18)", color: "bg-blue-900/30 text-blue-300 border border-blue-500/20" },
    { num: 3, label: "Phase 3 (Months 18-36)", color: "bg-white/5 text-white/60 border border-white/10" },
  ];

  return (
    <div className="space-y-5" data-testid="investor-projection-simulator">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: "Market Size", value: fmtNum(totalMarketSize), color: "text-blue-300 bg-blue-900/30 border-blue-500/20" },
          { label: "Target Providers", value: fmtNum(totalTarget), color: "text-orange-300 bg-orange-900/30 border-orange-500/20" },
          { label: "Est. Paying", value: fmtNum(Math.round(payingProviders)), color: "text-emerald-300 bg-emerald-900/30 border-emerald-500/20" },
          { label: "Projected MRR", value: fmt(projMRR), color: "text-green-300 bg-green-900/30 border-green-500/20" },
          { label: "Projected ARR", value: fmt(projARR), color: "text-indigo-300 bg-indigo-900/30 border-indigo-500/20" },
          { label: `Your Payout (${commissionPct}%)`, value: fmt(myPayout), color: "text-emerald-200 bg-emerald-800/40 border-emerald-400/30 font-bold" },
        ].map(({ label, value, color }) => (
          <div key={label} className={`rounded-xl border px-1.5 py-2.5 text-center ${color}`} data-testid={`sim-stat-${label.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}>
            <p className="text-sm sm:text-lg md:text-xl font-bold truncate">{value}</p>
            <p className="text-[8px] sm:text-[10px] md:text-xs font-medium opacity-80 leading-tight">{label}</p>
          </div>
        ))}
      </div>

      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardContent className="p-4 md:p-6">
          <div className="mb-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-400" /> City Provider Targets by Phase
            </h3>
            <p className="text-xs text-emerald-200/40 mt-1">Edit targets to explore different scenarios. Summary auto-calculates.</p>
          </div>
          <div className="space-y-3">
            {phases.map(phase => {
              const phaseCities = cities.filter(c => c.phase === phase.num);
              if (phaseCities.length === 0) return null;
              const isExpanded = expandedPhases[phase.num];
              return (
                <div key={phase.num}>
                  <button onClick={() => setExpandedPhases(p => ({ ...p, [phase.num]: !p[phase.num] }))} className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold ${phase.color} transition-colors`} data-testid={`phase-${phase.num}-toggle`}>
                    {phase.label} ({phaseCities.length} cities)
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {isExpanded && (
                    <div className="mt-1 space-y-1">
                      {phaseCities.map(city => (
                        <div key={city.city_id} className="px-3 py-2.5 bg-white/[0.02] border border-white/[0.04] rounded-lg hover:border-emerald-500/20 transition-colors" data-testid={`city-row-${city.city_id}`}>
                          {/* City name row */}
                          <div className="flex items-center gap-2 mb-1.5">
                            <MapPin className="w-3 h-3 text-emerald-400/60 shrink-0" />
                            <p className="text-xs font-medium text-white">{city.city}{city.state ? `, ${city.state}` : ''}</p>
                            <span className="text-[10px] text-emerald-200/30">{city.country}</span>
                            <Badge variant="outline" className={`text-[9px] shrink-0 ml-auto ${city.status === 'active' ? 'border-emerald-500/30 text-emerald-400 bg-emerald-900/20' : 'border-white/10 text-white/40'}`}>{city.status}</Badge>
                          </div>
                          {/* Market + Target row */}
                          <div className="flex items-center gap-2">
                            <div className="text-right flex-1">
                              <p className="text-sm font-bold text-emerald-400">{(city.total_market_size || 0).toLocaleString()}</p>
                              <p className="text-[10px] text-emerald-200/30 flex items-center justify-end gap-0.5"><Lock className="w-2.5 h-2.5" /> Market</p>
                            </div>
                            <div className="flex-1">
                              <input type="number" value={cityTargets[city.city_id] || 0} onChange={(e) => setCityTargets(prev => ({ ...prev, [city.city_id]: Math.max(0, Number(e.target.value)) }))} className="w-full text-right text-sm font-bold px-2 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/30" data-testid={`target-input-${city.city_id}`} />
                              <p className="text-[10px] text-emerald-200/30 text-right mt-0.5">Target</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between px-4 py-3 bg-emerald-900/30 border border-emerald-500/20 rounded-xl">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-emerald-200 font-medium">Your Commission Rate</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-emerald-300">{commissionPct}%</span>
          <Lock className="w-3.5 h-3.5 text-emerald-500/60" />
          <span className="text-xs text-emerald-400/60">Set by admin</span>
        </div>
      </div>
    </div>
  );
};

// --- Main Investor Portal Page ---
export default function InvestorPortalPage({ embeddedPortalId }) {
  const params = useParams();
  const portalId = embeddedPortalId || params.portalId;
  const navigate = useNavigate();
  const isEmbedded = !!embeddedPortalId;

  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("updates");
  const [isAdvocate, setIsAdvocate] = useState(false);

  const tabs = isAdvocate ? [...INVESTOR_TABS, ADVOCATE_TAB] : INVESTOR_TABS;

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/api/investor/portal/${portalId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          const genericRes = await fetch(`${API}/api/pilot/portal/${portalId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (genericRes.ok) {
            const data = await genericRes.json();
            setPortal(data.portal);
            setUser(data.user);
          } else {
            throw new Error("Portal not found");
          }
        } else {
          const data = await res.json();
          setPortal(data.portal);
          setUser(data.user);
        }

        if (token) {
          try {
            const advocateRes = await fetch(`${API}/api/user/advocate-status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (advocateRes.ok) {
              const advocateData = await advocateRes.json();
              setIsAdvocate(advocateData.is_advocate);
            }
          } catch (err) {}
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPortal();
  }, [portalId]);

  if (loading) {
    return (
      <div className={isEmbedded ? "flex items-center justify-center py-12" : "min-h-screen bg-[#0a1a0f] flex items-center justify-center"} style={isEmbedded ? {} : { backgroundImage: "radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.15) 0%, transparent 70%)" }}>
        <div className="text-center">
          <div className={`w-10 h-10 border-2 ${isEmbedded ? 'border-emerald-500' : 'border-emerald-400'} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
          <p className={isEmbedded ? "text-gray-500" : "text-emerald-200"}>Loading investor portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={isEmbedded ? "flex items-center justify-center py-12" : "min-h-screen bg-[#0a1a0f] flex items-center justify-center"} style={isEmbedded ? {} : { backgroundImage: "radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.15) 0%, transparent 70%)" }}>
        <Card className={isEmbedded ? "max-w-md" : "max-w-md bg-white/5 border-emerald-500/30 backdrop-blur-xl"}>
          <CardContent className="p-8 text-center">
            <Mail className={`w-12 h-12 ${isEmbedded ? 'text-emerald-500' : 'text-emerald-400'} mx-auto mb-4`} />
            <h2 className={`text-xl font-semibold ${isEmbedded ? 'text-gray-800' : 'text-white'} mb-2`}>Access Required</h2>
            <p className={isEmbedded ? "text-gray-500 mb-6" : "text-emerald-200 mb-6"}>{error}</p>
            <Button onClick={() => navigate("/")} variant="outline" className="border-emerald-500 text-emerald-400">Return Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1a0f] relative overflow-hidden" data-testid="investor-portal-page">
      {/* Unified top bar for consistent navigation */}
      <UnifiedTopBar variant="user" />
      
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(ellipse at 50% -20%, rgba(16,185,129,0.2) 0%, transparent 60%), linear-gradient(135deg, rgba(16,185,129,0.05) 0%, transparent 50%), linear-gradient(225deg, rgba(34,197,94,0.05) 0%, transparent 50%)" }} />
      <div className="absolute top-0 left-0 w-full h-[200px] pointer-events-none" style={{ background: "linear-gradient(to bottom right, rgba(16,185,129,0.08) 0%, transparent 50%), linear-gradient(to bottom left, rgba(34,197,94,0.08) 0%, transparent 50%)" }} />

      <header className="relative z-10 border-b border-white/5 bg-black/30 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0">
              <PhygitalIcon className="w-8 h-8 sm:w-10 sm:h-10" variant="light" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-white tracking-tight text-sm sm:text-base truncate">
                {user ? `Investor Portal: ${user.name}` : 'Investor Portal'}
              </h1>
              <p className="text-[10px] sm:text-xs text-emerald-300/70">Phygital</p>
            </div>
          </div>
          {user && (
            <div className="text-right hidden sm:block">
              <p className="text-xs text-emerald-300/60">{user.email}</p>
            </div>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {FINANCIAL_METRICS.map((metric, idx) => (
            <div key={idx} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center backdrop-blur-sm">
              <p className="text-xl font-bold text-white">{metric.value}</p>
              <p className="text-emerald-300 text-xs font-medium mt-1">{metric.label}</p>
              <p className="text-white/30 text-[10px] mt-0.5">{metric.sublabel}</p>
            </div>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/[0.03] border border-white/[0.06] p-1 flex gap-1 backdrop-blur-sm overflow-x-auto scrollbar-hide">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-emerald-300/70 flex items-center gap-1.5 whitespace-nowrap shrink-0 text-xs px-2.5 py-1.5"
                  data-testid={`investor-tab-${tab.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Tab 1: Updates (Default) */}
          <TabsContent value="updates" className="space-y-6" data-testid="investor-updates-tab">
            <InvestorUpdates />
          </TabsContent>

          {/* Tab 2: Pitch Deck - break out of parent padding for full-bleed */}
          <TabsContent value="pitch_deck" className="-mx-4 sm:mx-0" data-testid="investor-pitch-tab">
            <PitchDeckSlides />
          </TabsContent>

          {/* Tab 3: Projections */}
          <TabsContent value="projections" className="space-y-8" data-testid="investor-projections-tab">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              Live Projection Simulator
            </h3>
            <InvestorProjectionSimulator investorPortalId={portalId} />
          </TabsContent>

          {/* Tab 3: Advocate (Conditional) */}
          {isAdvocate && (
            <TabsContent value="advocate" className="space-y-6" data-testid="investor-advocate-tab">
              <div className="bg-white rounded-2xl p-1 -mx-1">
                <AdvocatePortalPage embedded />
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>

      <footer className="relative z-10 border-t border-white/5 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white/20">
            <PhygitalIcon className="w-4 h-4" />
            <span className="text-xs">Phygital 2026</span>
          </div>
          <p className="text-[10px] text-white/15">Confidential</p>
        </div>
      </footer>
    </div>
  );
}
